import { useEffect, useRef, useState } from 'react';

export const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

export interface Shortcut {
  // Cmd on Mac, Ctrl elsewhere.
  mod?: boolean;
  shift?: boolean;
  // KeyboardEvent.code, so shortcuts work on any layout (e.g. Bulgarian Cyrillic) and Shift+; is not ":".
  code: string;
  label: string;
}

// Ctrl/Cmd+N, T and W never reach a web page, hence the Shift combinations.
export const SHORTCUTS: Record<
  'newChat' | 'search' | 'toggleSidebar' | 'copyLastResponse' | 'focusInput' | 'showShortcuts', Shortcut
> = {
  newChat: { mod: true, shift: true, code: 'KeyO', label: 'New chat' },
  search: { mod: true, code: 'KeyK', label: 'Search conversations' },
  toggleSidebar: { mod: true, shift: true, code: 'KeyS', label: 'Toggle sidebar' },
  copyLastResponse: { mod: true, shift: true, code: 'Semicolon', label: 'Copy last response' },
  focusInput: { shift: true, code: 'Escape', label: 'Focus the message box' },
  showShortcuts: { mod: true, code: 'Slash', label: 'Show keyboard shortcuts' },
};

export const OPEN_SHORTCUTS_EVENT = 'cv:open-shortcuts';

const KEY_LABELS: Record<string, string> = { Semicolon: ';', Slash: '/', Escape: 'Esc' };

const keyLabel = (code: string) => KEY_LABELS[code] ?? code.replace(/^Key|^Digit/, '');

export function shortcutKeys(shortcut: Shortcut): string[] {
  const keys: string[] = [];
  if (shortcut.mod) keys.push(IS_MAC ? '⌘' : 'Ctrl');
  if (shortcut.shift) keys.push(IS_MAC ? '⇧' : 'Shift');
  keys.push(keyLabel(shortcut.code));

  return keys;
}

export const formatShortcut = (shortcut: Shortcut) => shortcutKeys(shortcut).join(IS_MAC ? '' : '+');

export function matchesShortcut(e: KeyboardEvent, shortcut: Shortcut): boolean {
  const mod = IS_MAC ? e.metaKey : e.ctrlKey;
  const otherMod = IS_MAC ? e.ctrlKey : e.metaKey;

  return e.code === shortcut.code
    && mod === !!shortcut.mod
    && e.shiftKey === !!shortcut.shift
    && !e.altKey
    && !otherMod;
}

export function useShortcut(shortcut: Shortcut, handler: () => void, enabled = true) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat || !matchesShortcut(e, shortcut)) return;
      e.preventDefault();
      handlerRef.current();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shortcut, enabled]);
}

const HINT_DELAY_MS = 500;

// Holding Cmd/Ctrl on its own reveals the hints. The delay and the cancel-on-any-other-key
// keep everyday Ctrl+C / Ctrl+Shift+Arrow from flashing them up.
export function useShortcutHints(): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const modKey = IS_MAC ? 'Meta' : 'Control';

    const hide = () => {
      clearTimeout(timer);
      timer = undefined;
      setVisible(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === modKey && !e.repeat) {
        clearTimeout(timer);
        timer = setTimeout(() => setVisible(true), HINT_DELAY_MS);
      } else if (e.key !== 'Shift' && e.key !== modKey) {
        hide();
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === modKey) hide();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('mousedown', hide);
    window.addEventListener('blur', hide);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('mousedown', hide);
      window.removeEventListener('blur', hide);
    };
  }, []);

  useEffect(() => {
    if (visible) {
      document.documentElement.setAttribute('data-shortcut-hints', 'on');
    } else {
      document.documentElement.removeAttribute('data-shortcut-hints');
    }
  }, [visible]);

  return visible;
}
